import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, Building2, CheckCircle2, ExternalLink, FileSpreadsheet, KeyRound,
  Loader2, RefreshCw, Search, ShieldCheck, Upload, UserPlus, X, DollarSign,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/CrudModule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const money = (v) => v == null || v === "" ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));

function Metric({ label, value, sub }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-heading text-2xl font-bold tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function badgeClass(value = "") {
  const v = value.toLowerCase();
  if (v.includes("high") || v.includes("nsf") || v.includes("not paid")) return "bg-red-50 text-red-700 ring-red-200";
  if (v.includes("issued") || v.includes("active") || v.includes("resolved") || v.includes("ready")) return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (v.includes("underwriting") || v.includes("waiting") || v.includes("payment")) return "bg-amber-50 text-amber-800 ring-amber-200";
  return "bg-secondary text-foreground ring-border";
}

function Pill({ children, value }) {
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${badgeClass(value || String(children))}`}>{children}</span>;
}

function CaseEditor({ row, onClose, onSaved, onLaunch }) {
  const [form, setForm] = useState({
    carrier: row.carrier || "",
    premium: row.premium ?? "",
    plan_type: row.plan_type || "",
    draft_date_raw: row.draft_date_raw || "",
    actual_policy_status: row.actual_policy_status || "",
    carrier_application_number: row.carrier_application_number || "",
    policy_number: row.policy_number || "",
    effective_date: row.effective_date || "",
    first_premium_status: row.first_premium_status || "",
    expected_commission: row.expected_commission ?? "",
    commission_received: row.commission_received ?? "",
    commission_received_date: row.commission_received_date || "",
    carrier_notes: row.carrier_notes || "",
    next_action: row.next_action || "",
    licensed_agent_action_required: !!row.licensed_agent_action_required,
    resolution_status: row.resolution_status || "",
    resolution_notes: row.resolution_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ["premium", "expected_commission", "commission_received"].forEach((k) => {
        payload[k] = payload[k] === "" ? null : Number(payload[k]);
      });
      const { data } = await api.put(`/book-recovery/cases/${row.id}`, payload);
      toast.success("Recovery case updated"); onSaved(data); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 p-3 sm:p-6 overflow-y-auto">
      <div className="mx-auto max-w-4xl rounded-xl border bg-card shadow-xl">
        <div className="sticky top-0 z-10 flex items-start justify-between border-b bg-card p-5 rounded-t-xl">
          <div>
            <div className="text-lg font-semibold">{row.client_name}</div>
            <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
              <span>{row.display_id}</span><span>•</span><span>Source: {row.source_status_raw || "Unknown"}</span><span>•</span><span>{row.source_vendor_name || "Unlinked source"}</span>
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-secondary"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
            <strong>Spreadsheet status is not carrier truth.</strong> Use the carrier portal and client follow-up to establish what actually happened before closing this case.
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-lg border p-4">
              <div className="mb-3 font-semibold text-sm">What is still missing</div>
              <div className="flex flex-wrap gap-2">
                {(row.missing_items || []).length ? row.missing_items.map((x) => <Pill key={x}>{x}</Pill>) : <Pill value="ready">No unresolved fields</Pill>}
              </div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="mb-3 font-semibold text-sm">Recommended actions</div>
              <ol className="space-y-2 text-xs text-muted-foreground list-decimal pl-4">
                {(row.required_actions || []).map((x, i) => <li key={i}>{x}</li>)}
              </ol>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4">
            <div>
              <div className="font-semibold text-sm">{row.carrier || "Carrier not identified"}</div>
              <div className="text-xs text-muted-foreground">
                {row.carrier_access?.available ? `Andis's Vault has a matching carrier login (${row.carrier_access.status || "stored"}).` : "No matching carrier credential is currently available in the Vault."}
              </div>
            </div>
            <Button variant={row.carrier_access?.available ? "default" : "outline"} onClick={() => onLaunch(row)}>
              {row.carrier_access?.available ? <><ExternalLink className="mr-1.5 h-4 w-4" /> Open Carrier Portal</> : <><KeyRound className="mr-1.5 h-4 w-4" /> Go to Credential Vault</>}
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div><Label>Carrier</Label><Input value={form.carrier} onChange={(e) => set("carrier", e.target.value)} /></div>
            <div><Label>Plan / Product</Label><Input value={form.plan_type} onChange={(e) => set("plan_type", e.target.value)} /></div>
            <div><Label>Monthly Premium</Label><Input type="number" step="0.01" value={form.premium} onChange={(e) => set("premium", e.target.value)} /></div>
            <div><Label>Carrier App #</Label><Input value={form.carrier_application_number} onChange={(e) => set("carrier_application_number", e.target.value)} /></div>
            <div><Label>Policy #</Label><Input value={form.policy_number} onChange={(e) => set("policy_number", e.target.value)} /></div>
            <div><Label>Effective Date</Label><Input type="date" value={form.effective_date} onChange={(e) => set("effective_date", e.target.value)} /></div>
            <div><Label>Draft Date</Label><Input value={form.draft_date_raw} onChange={(e) => set("draft_date_raw", e.target.value)} /></div>
            <div>
              <Label>Authoritative Carrier Status</Label>
              <Select value={form.actual_policy_status || "__blank__"} onValueChange={(v) => set("actual_policy_status", v === "__blank__" ? "" : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__blank__">Not verified</SelectItem>
                  {["Pending", "Underwriting", "Approved", "Issued", "In Force", "Lapsed", "Cancelled", "Declined", "Withdrawn", "Not Taken"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>First Premium / Draft</Label>
              <Select value={form.first_premium_status || "__blank__"} onValueChange={(v) => set("first_premium_status", v === "__blank__" ? "" : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__blank__">Not verified</SelectItem>
                  {["Paid", "Pending", "NSF", "Returned", "Redraft Scheduled", "Waived / N/A"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Expected Commission</Label><Input type="number" step="0.01" value={form.expected_commission} onChange={(e) => set("expected_commission", e.target.value)} /></div>
            <div><Label>Commission Received</Label><Input type="number" step="0.01" value={form.commission_received} onChange={(e) => set("commission_received", e.target.value)} /></div>
            <div><Label>Commission Date</Label><Input type="date" value={form.commission_received_date} onChange={(e) => set("commission_received_date", e.target.value)} /></div>
          </div>

          <div><Label>Carrier Research Notes</Label><Textarea value={form.carrier_notes} onChange={(e) => set("carrier_notes", e.target.value)} placeholder="Document authoritative carrier status, requirements, draft result, or administrative findings." /></div>
          <div><Label>Next Action</Label><Input value={form.next_action} onChange={(e) => set("next_action", e.target.value)} /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.licensed_agent_action_required} onChange={(e) => set("licensed_agent_action_required", e.target.checked)} /> Requires Andis's licensed-agent action</label>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Resolution</Label>
              <Select value={form.resolution_status || "__blank__"} onValueChange={(v) => set("resolution_status", v === "__blank__" ? "" : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__blank__">Still open</SelectItem>
                  {["Recovered / In Force", "Resolved — Paid", "Resolved — Reinstated", "Closed — Lapsed", "Closed — Cancelled", "Closed — Declined", "Closed — Client Declined", "Closed — No Further Action"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Resolution Notes</Label><Input value={form.resolution_notes} onChange={(e) => set("resolution_notes", e.target.value)} /></div>
          </div>

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Recovery Work"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BookRecovery() {
  const { user } = useAuth();
  const recoveryRef = useRef();
  const credentialRef = useRef();
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [search, setSearch] = useState("");
  const [bucket, setBucket] = useState("all");
  const [editing, setEditing] = useState(null);

  // AI service follow-up batch repair (admin only)
  const [prep, setPrep] = useState(null);
  const [prepBusy, setPrepBusy] = useState(false);
  const [prepDueToday, setPrepDueToday] = useState(false);
  const [prepResult, setPrepResult] = useState(null);
  const isAdmin = user?.role === "admin";

  const runPrepPreview = async () => {
    setPrepBusy(true); setPrepResult(null);
    try {
      const { data } = await api.post("/book-recovery/followups/preview", {});
      setPrep(data);
      toast.success(`Previewed ${data.total_cases} recovery case(s) — no changes made`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPrepBusy(false); }
  };

  const runPrepPrepare = async () => {
    if (!prep) return;
    const caseIds = (prep.rows || []).filter((r) => r.included).map((r) => r.case_id);
    if (!caseIds.length) { toast.error("No eligible cases to prepare in this preview."); return; }
    setPrepBusy(true);
    try {
      const { data } = await api.post("/book-recovery/followups/prepare", { case_ids: caseIds, due_today: prepDueToday });
      setPrepResult(data);
      let triageText = "";
      try {
        const { data: triage } = await api.post("/voice-ai/auto/triage");
        const ready = (triage.by_readiness?.AI_READY || 0) + (triage.by_readiness?.URGENT_AI_READY || 0);
        const permission = triage.by_readiness?.AI_PERMISSION_REQUIRED || 0;
        triageText = ` · ${ready} AI-ready, ${permission} need AI permission`;
      } catch (_) {
        triageText = " · readiness refresh needs attention";
      }
      toast.success(`Prepared ${data.counts.applied} case(s): ${data.counts.contacts_created} contact(s), ${data.counts.orphans_repaired} orphan link(s) repaired${triageText}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPrepBusy(false); }
  };

  const [vendorInvite, setVendorInvite] = useState({ contact_name: "", company_name: "Empower BPO", email: "", aliases: ["Empower BPO"] });
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const [credAnalysis, setCredAnalysis] = useState(null);
  const [credBusy, setCredBusy] = useState(false);
  const [credOwner, setCredOwner] = useState("");

  const load = () => {
    Promise.all([
      api.get("/book-recovery/cases").catch(() => ({ data: [] })),
      api.get("/book-recovery/summary").catch(() => ({ data: null })),
      api.get("/book-recovery/vendors").catch(() => ({ data: [] })),
    ]).then(([c, s, v]) => { setRows(c.data || []); setSummary(s.data); setVendors(v.data || []); });
  };
  useEffect(load, []);

  const analyzeRecovery = async (file) => {
    setAnalyzing(true); setAnalysis(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/book-recovery/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(data);
      setSelectedVendor(data.inferred_vendor?.id || "");
      toast.success(`${data.summary.total_cases} recovery cases analyzed`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  const commitRecovery = async () => {
    if (!analysis) return;
    setCommitting(true);
    try {
      const vendor = vendors.find((v) => v.id === selectedVendor);
      const { data } = await api.post("/book-recovery/commit", {
        session_id: analysis.session_id,
        source_vendor_id: selectedVendor || "",
        source_vendor_name: vendor?.name || analysis.source_vendor_name || "",
      });
      toast.success(`Recovery workspace created: ${data.inserted} new, ${data.updated} refreshed`);
      setAnalysis(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCommitting(false); }
  };

  const inviteVendor = async () => {
    if (!vendorInvite.contact_name || !vendorInvite.company_name || !vendorInvite.email) { toast.error("Contact, company and email are required"); return; }
    setInviting(true);
    try {
      const { data } = await api.post("/intake/vendors/invite", vendorInvite);
      setInviteResult(data);
      setSelectedVendor(data.vendor.id);
      toast.success(data.email_status?.sent ? "Vendor invitation sent" : "Vendor account created; email delivery needs review");
      if (analysis?.source_vendor_name) {
        await api.post("/book-recovery/link-vendor", { vendor_id: data.vendor.id, source_vendor_name: analysis.source_vendor_name }).catch(() => {});
      } else {
        await api.post("/book-recovery/link-vendor", { vendor_id: data.vendor.id, source_vendor_name: vendorInvite.company_name }).catch(() => {});
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setInviting(false); }
  };

  const analyzeCredentials = async (file) => {
    setCredBusy(true); setCredAnalysis(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/credential-import/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCredAnalysis(data); setCredOwner(data.suggested_owner_user_id || "");
      toast.success(`${data.recognized_records} carrier credential records recognized`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCredBusy(false); }
  };

  const commitCredentials = async () => {
    if (!credAnalysis) return;
    if (user.role !== "agent" && !credOwner) { toast.error("Select the agent who owns these credentials"); return; }
    setCredBusy(true);
    try {
      const { data } = await api.post("/credential-import/commit", {
        session_id: credAnalysis.session_id,
        owner_user_id: credOwner,
        sheets: credAnalysis.sheets.map((x) => x.name),
      });
      toast.success(`Credential Vault updated for ${data.owner_name}: ${data.inserted} new, ${data.updated} updated`);
      setCredAnalysis(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCredBusy(false); }
  };

  const launchCarrier = async (row) => {
    const id = row.carrier_access?.credential_id;
    if (!id) { window.location.href = "/vault"; return; }
    try {
      const { data } = await api.post(`/credentials/${id}/launch`);
      if (data.link) window.open(data.link, "_blank", "noopener");
      else toast.error("This Vault record does not have a portal URL yet.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    let out = rows;
    if (bucket !== "all") out = out.filter((r) => r.recovery_bucket === bucket);
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
    }
    return out;
  }, [rows, bucket, search]);
  const buckets = [...new Set(rows.map((r) => r.recovery_bucket).filter(Boolean))];

  return (
    <div data-testid="book-recovery-page">
      <PageHeader
        title="Existing Book Recovery"
        subtitle="Reconcile the spreadsheet against carrier truth, payment/draft outcomes and commissions — case by case."
      >
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="mr-1.5 h-4 w-4" /> Refresh</Button>
      </PageHeader>

      <div className="mb-5 rounded-xl border border-[#B28A3D]/40 bg-[#B28A3D]/10 p-4 text-sm">
        <strong>The source workbook is evidence, not the finish line.</strong> “Approved” in the spreadsheet does not prove issued/in-force, paid, or commissioned. Fortunate Operations turns each row into a recovery case and shows what must be verified next.
      </div>

      {summary && rows.length > 0 && (
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Metric label="Cases" value={summary.total} />
          <Metric label="Open" value={summary.open} />
          <Metric label="Resolved" value={summary.resolved} />
          <Metric label="Payment Problems" value={summary.payment_problems} />
          <Metric label="Known Monthly Premium" value={money(summary.known_monthly_premium)} />
          <Metric label="Known Annualized Premium" value={money(summary.known_annualized_premium)} sub="Premium context — not commission" />
        </div>
      )}

      {isAdmin && (
        <div className="mb-6 rounded-xl border bg-card p-5" data-testid="followup-prep-panel">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 font-semibold"><ShieldCheck className="h-4 w-4" /> AI Service Follow-Up Preparation</div>
              <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
                Link every recovery case to a resolvable service contact and prepare its follow-up task. Preview makes
                <strong> no changes</strong>. Preparation links contacts and tasks only — it never creates policies or commissions,
                never dials/texts/emails, and never enables the AI backlog. Sophia stays gated by voice + knowledge acceptance.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <input type="checkbox" data-testid="prep-due-today" checked={prepDueToday} onChange={(e) => setPrepDueToday(e.target.checked)} />
                Schedule due today
              </label>
              <Button variant="outline" size="sm" onClick={runPrepPreview} disabled={prepBusy} data-testid="prep-preview-btn">
                {prepBusy && !prepResult ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Search className="mr-1.5 h-4 w-4" />} Preview Plan
              </Button>
              <Button size="sm" onClick={runPrepPrepare} disabled={prepBusy || !prep} data-testid="prep-prepare-btn">
                {prepBusy && prep ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />} Prepare Follow-Ups
              </Button>
            </div>
          </div>

          {prep && (
            <div className="mt-5 space-y-4 border-t pt-5" data-testid="prep-preview-result">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                <Metric label="Cases Accounted" value={prep.counts.total_cases} />
                <Metric label="Will Prepare" value={prep.counts.included} />
                <Metric label="Excluded" value={prep.counts.excluded} sub="see reasons below" />
                <Metric label="Verified" value={prep.counts.verified} sub={`${prep.counts.unverified_neutral} neutral verify`} />
                <Metric label="Contacts to Create" value={prep.counts.contact_creates} sub={`${prep.counts.contact_links} reused`} />
                <Metric label="Orphan Links to Repair" value={prep.counts.orphan_repairs} />
              </div>
              <div className="grid grid-cols-2 gap-3 text-[11px] text-muted-foreground md:grid-cols-4">
                <div>Closed / no action: <strong>{prep.counts.excluded_closed}</strong></div>
                <div>Missing contact facts: <strong>{prep.counts.excluded_missing_contact}</strong></div>
                <div>Ambiguous (human review): <strong>{prep.counts.excluded_ambiguous}</strong></div>
                <div>Suppressed / no outbound: <strong>{prep.counts.suppressed_no_outbound}</strong></div>
              </div>
              <div className="max-h-80 overflow-auto rounded-lg border">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-secondary/60 text-left"><tr>
                    <th className="p-2">Client</th><th className="p-2">Purpose</th><th className="p-2">Verified</th>
                    <th className="p-2">Contact</th><th className="p-2">Follow-Up</th><th className="p-2">Status / Reason</th>
                  </tr></thead>
                  <tbody>
                    {prep.rows.map((r) => (
                      <tr key={r.case_id} className="border-t align-top">
                        <td className="p-2 font-medium">{r.client_name || "—"}<div className="text-[10px] text-muted-foreground">{r.display_id}</div></td>
                        <td className="p-2">{r.service_purpose}</td>
                        <td className="p-2">{r.verified ? <span className="text-emerald-700">verified</span> : <span className="text-amber-700">neutral verify</span>}</td>
                        <td className="p-2">{r.contact_action}</td>
                        <td className="p-2">{r.event_type ? r.followup_action : "none"}</td>
                        <td className="p-2">{r.included
                          ? <span className="text-emerald-700">{r.suppressed ? "prepare (no outbound — suppressed)" : "will prepare"}</span>
                          : <span className="text-amber-800">{r.exclusion_reason}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {prepResult && (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-xs text-emerald-950" data-testid="prep-apply-result">
              <div className="font-semibold">Preparation complete — no calls, texts, or emails were sent.</div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                <span>Applied: <strong>{prepResult.counts.applied}</strong></span>
                <span>Contacts created: <strong>{prepResult.counts.contacts_created}</strong></span>
                <span>Contacts reused: <strong>{prepResult.counts.contacts_linked}</strong></span>
                <span>Orphan links repaired: <strong>{prepResult.counts.orphans_repaired}</strong></span>
                <span>Tasks created: <strong>{prepResult.counts.followups_created}</strong></span>
                <span>Tasks updated: <strong>{prepResult.counts.followups_updated}</strong></span>
                <span>Skipped (needs review): <strong>{prepResult.counts.skipped_needs_review}</strong></span>
                {prepResult.rejected?.length > 0 && <span className="text-red-700">Rejected (not this agency): <strong>{prepResult.rejected.length}</strong></span>}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-3 mb-6">
        <div className="xl:col-span-2 rounded-xl border bg-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 font-semibold"><FileSpreadsheet className="h-4 w-4" /> Analyze Recovery Workbook</div>
              <p className="mt-1 text-xs text-muted-foreground">Use the actual BPO/existing-book spreadsheet. Nothing becomes an authoritative policy until carrier verification supports it.</p>
            </div>
            <input ref={recoveryRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => e.target.files?.[0] && analyzeRecovery(e.target.files[0])} />
            <Button onClick={() => recoveryRef.current?.click()} disabled={analyzing}>
              {analyzing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} Upload & Analyze
            </Button>
          </div>

          {analysis && (
            <div className="mt-5 space-y-4 border-t pt-5">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Metric label="Cases Found" value={analysis.summary.total_cases} />
                <Metric label="Approved — Unverified" value={analysis.summary.approved_unverified} />
                <Metric label="Payment Unknown" value={analysis.summary.payment_unknown} />
                <Metric label="NSF / Not Paid" value={analysis.summary.payment_problems} />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {(analysis.summary.carriers || []).map((carrier) => {
                  const c = analysis.summary.credential_readiness?.[carrier] || {};
                  return <div key={carrier} className={`rounded-lg border p-3 ${c.available ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
                    <div className="font-medium text-sm">{carrier}</div>
                    <div className="mt-1 text-xs">{c.available ? "Vault login available" : "Credential missing"}</div>
                  </div>;
                })}
              </div>
              <div className="rounded-lg border p-4">
                <Label>Lead Vendor / Source Relationship</Label>
                <Select value={selectedVendor || "__none__"} onValueChange={(v) => setSelectedVendor(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Not linked yet — source detected as {analysis.source_vendor_name}</SelectItem>
                    {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}{v.contact_name ? ` — ${v.contact_name}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-[11px] text-muted-foreground">Linking the vendor lets that vendor see sanitized lead status after invitation — never Andis's private operations.</p>
              </div>
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/50"><tr><th className="p-2 text-left">Client</th><th className="p-2 text-left">Carrier</th><th className="p-2 text-left">Source Status</th><th className="p-2 text-left">Payment</th><th className="p-2 text-left">Recovery Action</th><th className="p-2 text-right">Missing</th></tr></thead>
                  <tbody>{analysis.cases.slice(0, 12).map((r, i) => <tr key={i} className="border-t"><td className="p-2 font-medium">{r.client_name}</td><td className="p-2">{r.carrier || "Missing"}</td><td className="p-2">{r.source_status_normalized}</td><td className="p-2"><Pill value={r.payment_status_normalized}>{r.payment_status_normalized}</Pill></td><td className="p-2 max-w-[320px]">{r.required_actions?.[0]}</td><td className="p-2 text-right">{r.missing_items?.length || 0}</td></tr>)}</tbody>
                </table>
              </div>
              {analysis.cases.length > 12 && <div className="text-xs text-muted-foreground">Showing 12 of {analysis.cases.length} analyzed cases. All are committed to the Recovery workspace.</div>}
              <div className="flex justify-end"><Button onClick={commitRecovery} disabled={committing}>{committing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1.5 h-4 w-4" />} Create Recovery Workspace</Button></div>
            </div>
          )}
        </div>

        <div className="rounded-xl border bg-card p-5">
          <div className="flex items-center gap-2 font-semibold"><UserPlus className="h-4 w-4" /> Invite Lead Vendor</div>
          <p className="mt-1 mb-4 text-xs text-muted-foreground">Kevin can receive a real invitation and then see the status of only the leads linked to his vendor account.</p>
          <div className="space-y-3">
            <div><Label>Contact Name</Label><Input value={vendorInvite.contact_name} onChange={(e) => setVendorInvite((v) => ({ ...v, contact_name: e.target.value }))} placeholder="Kevin" /></div>
            <div><Label>Company / Lead Source</Label><Input value={vendorInvite.company_name} onChange={(e) => setVendorInvite((v) => ({ ...v, company_name: e.target.value, aliases: [e.target.value] }))} /></div>
            <div><Label>Email</Label><Input type="email" value={vendorInvite.email} onChange={(e) => setVendorInvite((v) => ({ ...v, email: e.target.value }))} /></div>
            <Button className="w-full" onClick={inviteVendor} disabled={inviting}>{inviting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <UserPlus className="mr-1.5 h-4 w-4" />} Send Vendor Invitation</Button>
            {inviteResult && (
              <div className={`rounded-lg border p-3 text-xs ${inviteResult.email_status?.sent ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
                <div className="font-medium">{inviteResult.email_status?.sent ? "Invitation accepted by email provider" : `Email status: ${inviteResult.email_status?.status || "needs review"}`}</div>
                <div className="mt-1 text-muted-foreground">Vendor account: {inviteResult.vendor.name}. The activation link is one-time and expires.</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-xl border bg-card p-5 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 font-semibold"><KeyRound className="h-4 w-4" /> Import Carrier Credentials</div>
            <p className="mt-1 text-xs text-muted-foreground">Upload the workbook that contains carrier usernames/passwords/portal links. Passwords are encrypted before staging and assigned to the responsible agent's Vault.</p>
          </div>
          <input ref={credentialRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => e.target.files?.[0] && analyzeCredentials(e.target.files[0])} />
          <Button variant="outline" onClick={() => credentialRef.current?.click()} disabled={credBusy}>{credBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} Analyze Credential Workbook</Button>
        </div>
        {credAnalysis && (
          <div className="mt-5 border-t pt-5 space-y-4">
            <div className="flex flex-wrap gap-2">
              {credAnalysis.sheets.map((s) => <Pill key={s.name} value="ready">{s.name}: {s.rows} credential row(s)</Pill>)}
            </div>
            {user.role !== "agent" && (
              <div className="max-w-md">
                <Label>Credential Owner</Label>
                <Select value={credOwner || "__none__"} onValueChange={(v) => setCredOwner(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">Select responsible agent</SelectItem>{credAnalysis.agent_options.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} — {a.email}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid gap-3 lg:grid-cols-2">
              {credAnalysis.sheets.map((s) => <div key={s.name} className="rounded-lg border p-3"><div className="font-medium text-sm">{s.name}</div><div className="mt-2 space-y-1 text-xs text-muted-foreground">{s.preview.slice(0, 4).map((r, i) => <div key={i}>{r.carrier} · {r.username} · {r.password} · {r.portal !== "—" ? "portal saved" : "no portal URL"}</div>)}</div>{s.excluded_columns?.length > 0 && <div className="mt-2 text-[11px] text-red-700">Excluded by security policy: {s.excluded_columns.join(", ")}</div>}</div>)}
            </div>
            <Button onClick={commitCredentials} disabled={credBusy || (user.role !== "agent" && !credOwner)}><KeyRound className="mr-1.5 h-4 w-4" /> Import to Credential Vault</Button>
          </div>
        )}
      </div>

      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="flex flex-col gap-3 border-b p-4 lg:flex-row lg:items-center lg:justify-between">
          <div><div className="font-semibold">Recovery Cases</div><div className="text-xs text-muted-foreground">Open a row, verify carrier facts, document payment/commission truth, and close the recovery loop.</div></div>
          <div className="flex flex-wrap gap-2">
            <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" /><Input className="pl-8 w-56" placeholder="Search cases…" value={search} onChange={(e) => setSearch(e.target.value)} /></div>
            <Select value={bucket} onValueChange={setBucket}><SelectTrigger className="w-52"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All recovery buckets</SelectItem>{buckets.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="py-14 text-center text-sm text-muted-foreground">No recovery cases have been committed yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-secondary/50 text-left uppercase tracking-wider text-muted-foreground"><tr><th className="p-3">Client</th><th className="p-3">Carrier</th><th className="p-3">Premium</th><th className="p-3">Source</th><th className="p-3">Recovery</th><th className="p-3">Payment</th><th className="p-3">Missing</th><th className="p-3">Carrier Access</th></tr></thead>
              <tbody>{filtered.map((r) => <tr key={r.id} onClick={() => setEditing(r)} className="border-t cursor-pointer hover:bg-secondary/30"><td className="p-3"><div className="font-medium text-sm">{r.client_name}</div><div className="text-[10px] text-muted-foreground">{r.display_id}</div></td><td className="p-3">{r.carrier || <span className="text-red-600">Missing</span>}</td><td className="p-3 tabular-nums">{money(r.premium)}</td><td className="p-3"><Pill value={r.source_status_normalized}>{r.source_status_normalized || "Unknown"}</Pill></td><td className="p-3"><Pill value={r.priority}>{r.recovery_bucket}</Pill></td><td className="p-3"><Pill value={r.payment_status_normalized}>{r.payment_status_normalized || "Unknown"}</Pill></td><td className="p-3 text-center">{r.missing_items?.length || 0}</td><td className="p-3">{r.carrier_access?.available ? <span className="text-emerald-700">Vault ready</span> : <span className="text-amber-700">Credential needed</span>}</td></tr>)}</tbody>
            </table>
          </div>
        )}
      </div>

      {editing && <CaseEditor row={editing} onClose={() => setEditing(null)} onSaved={() => load()} onLaunch={launchCarrier} />}
    </div>
  );
}
